import { nextTick, type Ref } from 'vue'

const REG_DEEP_LINK_KEY = 'auth-deep-link:reg'

/**
 * True from the moment a `?reg=1` arrival is detected until the page is left.
 *
 * WelcomePopup reads it to stay out of the way: it renders at `z-[100]`, above
 * the login modal's `z-50`, so on a first visit it would completely cover the
 * form the link exists to open. Suppressing it does not burn the popup's
 * once-per-browser flag — that is only written when the user closes it — so a
 * later organic visit still gets the intro.
 *
 * Shared state rather than a `route.query.reg` read inside WelcomePopup itself,
 * because the popup lives inside a `.client` component that mounts AFTER
 * app.vue's `onMounted` has already stripped `reg` from the URL. `useState`
 * rather than a module-level ref so it stays per-request on the server instead
 * of leaking between SSR renders.
 */
export function useRegDeepLinkActive(): Ref<boolean> {
  return useState<boolean>(REG_DEEP_LINK_KEY, () => false)
}

/**
 * `?reg=1` opens the login / sign-up modal on arrival, so an ad creative or an
 * email CTA can drop a visitor straight into registration instead of onto a
 * page where they still have to find the button.
 *
 * Mounted globally in `app.vue` rather than on a single page: the param is meant
 * to be appendable to whatever URL marketing is already sending traffic to.
 *
 * Mirrors useDepositDeepLink (`?action=deposit`) and the `?welcome=1` popup —
 * a composable rather than inline app code so the query-string behaviour is
 * testable without mounting the whole app.
 */
export function useAuthDeepLink(): { init: () => Promise<void> } {
  const router = useRouter()
  const uiStore = useUIStore()
  const userStore = useUserStore()
  // Resolved here, in setup: useState needs a Nuxt instance, and init() runs
  // from onMounted and then past several awaits.
  const regDeepLinkActive = useRegDeepLinkActive()

  async function init(): Promise<void> {
    const query = router.currentRoute.value.query
    // Exact match only. vue-router hands back an array for a repeated param, so
    // a loose check would let a hand-crafted `?reg=1&reg=x` through.
    if (query.reg !== '1') return

    // Synchronously, before the first await: the welcome popup decides whether
    // to show itself off `authInitialized`, which can settle at any point.
    regDeepLinkActive.value = true

    // Strip BEFORE opening, not after: LoginModal closes itself on any
    // `route.fullPath` change, so a replace() that lands after the open would
    // immediately shut the modal again. Awaiting the navigation (plus a tick for
    // that watcher to flush) keeps the two in a fixed order. Stripping also
    // stops a reload or a shared link from re-triggering registration, and the
    // rest of the query — `ref`, `utm_*` — is carried over untouched so
    // referral/attribution capture still sees it. `hash` is passed through
    // because a location object without one resolves to an empty hash.
    const { reg: _reg, ...rest } = query
    try {
      await router.replace({ query: rest, hash: router.currentRoute.value.hash })
      await nextTick()
    } catch {
      // A rejected navigation (a guard that throws) must not cost the visitor
      // the modal — the welcome popup is already suppressed at this point, so
      // bailing here would leave them looking at nothing at all. Worst case the
      // param survives in the URL and a reload reopens the modal.
    }

    // Session restoration runs detached from `plugins/auth.client.ts`, so
    // `isAuthenticated` is still false for a returning user at page load —
    // reading it without waiting would flash the login modal at someone who is
    // already signed in.
    await userStore.waitForAuth()
    if (userStore.isAuthenticated) return

    uiStore.openLoginModal()
  }

  return { init }
}
